import {useCallback, useState} from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn,
  type SignInOutput,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import {useLocation, useNavigate} from "react-router-dom";
import {AuthSettings} from "../types/auth";
import {urls} from "routes/urls";
import {useAuthContext} from "../contexts/AuthContext";
import {AxiosResponse} from "axios";
import {axiosPartnerPlatformInstance} from "../api/ServerApiManager";
import {applyAmplifyClientId} from "./useAmplifyConfig";

const handleNetworkError = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Unknown error occurred";
};

export const useAuth = () => {
  const navigate = useNavigate();
  const {email, setEmail, checkAuth, setSettings, error, setError, clearError, setPartnerCode} = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<SignInOutput | null>(null);
  const {state} = useLocation();

  const fetchSettings = useCallback(
    async (email: string) => {
      setIsLoading(true);
      clearError();
      try {
        const response = await axiosPartnerPlatformInstance.get<string, AxiosResponse<AuthSettings>>(
          `/auth/get-settings-by-email?email=${email}`
        );

        setSettings(response.data);
        setEmail(email);
        navigate(urls.login.path, {state});
      } finally {
        setIsLoading(false);
      }
    },
    [clearError, navigate, setEmail, setSettings]
  );
  const handlePasswordSignIn = async (password: string) => {
    return executeWithLoading(async () => {
      const result = await signIn({
        username: email!,
        password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH",
        },
      });
      setSession(result);
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        navigate(urls.newPasswordRequired.url, {state: {...state, email}});
      } else if (result.isSignedIn) {
        await checkAuth();
        navigate(urls.home.url, {state});
      }
      return result;
    });
  };

  const handleSetNewPassword = async (newPassword: string, confirmPassword: string) => {
    return executeWithLoading(async () => {
      if (confirmPassword && newPassword !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });
      if (result.isSignedIn) {
        window.location.href = urls.auth.url;
      }
      return result;
    });
  };

  const handleSamlSignIn = async (provider: string) => {
    executeWithLoading(async () => {
      await signInWithRedirect({
        provider: {
          custom: provider,
        },
      });
    });
  };

  const handleResetPassword = async ({email}: {email: string}) => {
    executeWithLoading(async () => {
      try {
        const {data: emailSettings} = await axiosPartnerPlatformInstance.get<string, AxiosResponse<AuthSettings>>(
          `/auth/get-settings-by-email?email=${encodeURIComponent(email)}`
        );
        setSettings(emailSettings);
        applyAmplifyClientId(emailSettings.client_id);

        await resetPassword({username: email});
        navigate(urls.resetPasswordCode.url, {state: {email}});
      } catch (error: unknown) {
        const errorMessage = handleNetworkError(error);
        setError(errorMessage);
      }
    });
  };

  const handleVerifyResetCode = async (code: string) => {
    executeWithLoading(async () => {
      localStorage.setItem("reset_code", code);
      navigate(urls.resetPasswordNew.url, {state: {email}});
    });
  };

  const handleConfirmResetPassword = async (newPassword: string, confirmPassword: string) => {
    executeWithLoading(async () => {
      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }

      const code = localStorage.getItem("reset_code");
      if (!code) {
        setError("Verification code not found");
        return;
      }

      await confirmResetPassword({
        username: email,
        newPassword,
        confirmationCode: code,
      });

      localStorage.removeItem("reset_code");
      navigate(urls.login.url);
    });
  };

  const handleSignOut = async () => {
    executeWithLoading(async () => {
      await signOut();
      setEmail("");
      setSettings(undefined);
      setPartnerCode(undefined);
      setSession(null);
      // Performing a full page reload on purpose to clear all the state
      window.location.href = urls.auth.url;
    });
  };

  const checkSession = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens !== undefined;
    } catch {
      return false;
    }
  };

  const executeWithLoading = async <T>(operation: () => Promise<T>): Promise<T> => {
    setIsLoading(true);
    clearError();
    try {
      return await operation();
    } catch (err) {
      const errorMessage = handleNetworkError(err);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    error,
    isLoading,
    session,
    clearError,
    fetchSettings,
    handlePasswordSignIn,
    handleSamlSignIn,
    handleResetPassword,
    handleVerifyResetCode,
    handleConfirmResetPassword,
    handleSetNewPassword,
    handleSignOut,
    checkSession,
  };
};
